/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";

import { useDashboardExperiments } from "../api/dashboardApi";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SORT_BY,
} from "../constants/dashboardSortConstants";
import { PIPELINE_STAGE_CONFIG } from "../constants/dashboardStatusConstants";
import {
  computeLiveMetrics,
  computeStatusCounts,
  filterAndSortExperiments,
} from "../utils/dashboardUtils";
import { dashboardFilterSchema } from "../validations/dashboardSchema";

import type {
  DashboardStatusFilter,
  MainTab,
  SortOption,
  ViewMode,
} from "../types/dashboard.types";

const TRACKER_STATUS_ORDER = {
  active: 0,
  paused: 1,
  completed: 2,
} as const;

export const useDashboardState = () => {
  const {
    data: dashboardExperiments = [],
    isLoading,
    isFetching,
  } = useDashboardExperiments();
  const [mainTab, setMainTab] = useState<MainTab>("dashboard");
  const [statusFilter, setStatusFilter] =
    useState<DashboardStatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>(DEFAULT_SORT_BY);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const validatedFilters = useMemo(
    () =>
      dashboardFilterSchema.parse({
        statusFilter,
        searchQuery,
        sortBy,
      }),
    [searchQuery, sortBy, statusFilter],
  );

  const experiments = dashboardExperiments;

  const liveExperiments = useMemo(
    () => experiments.filter((experiment) => experiment.status === "active"),
    [experiments],
  );

  const trackerExperiments = useMemo(
    () =>
      [...experiments]
        .filter(
          (experiment) =>
            experiment.status === "active" ||
            experiment.status === "paused" ||
            experiment.status === "completed",
        )
        .sort(
          (a, b) =>
            TRACKER_STATUS_ORDER[a.status as keyof typeof TRACKER_STATUS_ORDER] -
              TRACKER_STATUS_ORDER[
                b.status as keyof typeof TRACKER_STATUS_ORDER
              ] ||
            a.updatedMinutesAgo - b.updatedMinutesAgo,
        ),
    [experiments],
  );

  const statusCounts = useMemo(
    () => computeStatusCounts(experiments),
    [experiments],
  );

  const pipelineStages = useMemo(
    () =>
      PIPELINE_STAGE_CONFIG.map((stage) => ({
        ...stage,
        count: statusCounts[stage.key] ?? 0,
      })),
    [statusCounts],
  );

  const liveMetrics = useMemo(
    () => computeLiveMetrics(liveExperiments),
    [liveExperiments],
  );

  const filteredExperiments = useMemo(
    () =>
      filterAndSortExperiments(
        experiments,
        validatedFilters.searchQuery,
        validatedFilters.statusFilter,
        validatedFilters.sortBy,
      ),
    [experiments, validatedFilters],
  );

  const pagedExperiments = useMemo(
    () =>
      filteredExperiments.slice(
        (currentPage - 1) * DEFAULT_ITEMS_PER_PAGE,
        currentPage * DEFAULT_ITEMS_PER_PAGE,
      ),
    [currentPage, filteredExperiments],
  );

  const totalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(filteredExperiments.length / DEFAULT_ITEMS_PER_PAGE),
      ),
    [filteredExperiments.length],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setCardsLoaded(true), 100);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return {
    experiments,
    liveExperiments,
    trackerExperiments,
    statusCounts,
    pipelineStages,
    liveMetrics,
    filteredExperiments,
    pagedExperiments,
    totalPages,
    mainTab,
    setMainTab,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    cardsLoaded,
    currentPage,
    setCurrentPage,
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    isLoading,
    isFetching,
  };
};
